import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { startAudio, stopAudio, startMicLoopback, stopMicLoopback, playTestTone } from "@/lib/hubAudio";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Phone, Play, Pause, Stop, SkipForward, Buildings, Notepad,
  Clock, CalendarBlank, Warning, CheckCircle, XCircle, ArrowRight, Hash, Keyboard, Microphone,
  ArrowClockwise, UsersThree, PencilSimple, FloppyDisk, Info
} from "@phosphor-icons/react";
import { toast } from "sonner";
import MrBanksPanel from "@/components/MrBanksPanel";
import TransferButton from "@/components/TransferButton";

const CALL_OUTCOMES = [
  { value: "no_answer", label: "No Answer" },
  { value: "voicemail", label: "Voicemail" },
  { value: "busy", label: "Busy" },
  { value: "wrong_number", label: "Wrong Number" },
  { value: "not_interested", label: "Not Interested" },
  { value: "callback", label: "Callback Requested" },
  { value: "interested", label: "Interested" },
  { value: "vacancy_identified", label: "Vacancy Identified" },
  { value: "new_opportunity", label: "New Client Opportunity" },
  { value: "existing_client", label: "Existing Client" },
  { value: "do_not_contact", label: "Do Not Contact" },
];

const STATUS_DISPLAY = {
  calling: { label: "Calling...", color: "bg-yellow-500", icon: Phone },
  requesting_mic: { label: "Requesting microphone...", color: "bg-yellow-500", icon: Microphone },
  connecting: { label: "Connecting...", color: "bg-yellow-500", icon: Phone },
  ringing: { label: "Ringing...", color: "bg-yellow-400", icon: Phone },
  connected: { label: "Connected", color: "bg-green-500", icon: CheckCircle },
  answered: { label: "Connected", color: "bg-green-500", icon: CheckCircle },
  ended: { label: "Call ended", color: "bg-gray-500", icon: XCircle },
  no_answer: { label: "No answer", color: "bg-red-400", icon: XCircle },
  busy: { label: "Busy", color: "bg-orange-500", icon: Warning },
  failed: { label: "Failed", color: "bg-red-600", icon: Warning },
  error: { label: "Error", color: "bg-red-600", icon: Warning },
  idle: { label: "Ready", color: "bg-[#0F172A]", icon: Phone },
};

// ─── DTMF tone frequencies (ITU-T standard dual-tone) ───
const DTMF_FREQ = {
  "1": [697, 1209], "2": [697, 1336], "3": [697, 1477],
  "4": [770, 1209], "5": [770, 1336], "6": [770, 1477],
  "7": [852, 1209], "8": [852, 1336], "9": [852, 1477],
  "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
};
let _dtmfCtx = null;
function playDtmfTone(digit) {
  try {
    if (!_dtmfCtx) _dtmfCtx = new (window.AudioContext || window.webkitAudioContext)();
    const ctx = _dtmfCtx;
    if (ctx.state === "suspended") ctx.resume();
    const freqs = DTMF_FREQ[digit];
    if (!freqs) return;
    const gain = ctx.createGain();
    gain.gain.value = 0.15;
    gain.connect(ctx.destination);
    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    osc1.type = "sine"; osc1.frequency.value = freqs[0];
    osc2.type = "sine"; osc2.frequency.value = freqs[1];
    osc1.connect(gain); osc2.connect(gain);
    const now = ctx.currentTime;
    osc1.start(now); osc2.start(now);
    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
    osc1.stop(now + 0.16); osc2.stop(now + 0.16);
  } catch (_) { /* AudioContext not available */ }
}

const KEYPAD_LAYOUT = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

const KEYPAD_LABELS = {
  "1": "", "2": "ABC", "3": "DEF",
  "4": "GHI", "5": "JKL", "6": "MNO",
  "7": "PQRS", "8": "TUV", "9": "WXYZ",
  "*": "", "0": "+", "#": "",
};

function DtmfKeypad({ callId, disabled }) {
  const [sending, setSending] = useState("");

  const press = async (digit) => {
    playDtmfTone(digit);
    setSending(digit);
    try {
      await api.post(`/telephony/call/${callId}/dtmf`, { digit });
    } catch (err) {
      toast.error("DTMF failed: " + (err?.response?.data?.error || err?.response?.data?.detail || "unknown"));
    }
    setTimeout(() => setSending(""), 200);
  };

  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="dtmf-keypad">
      <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-3">
        <Keyboard size={16} className="text-[#C5A059]" /> Keypad
      </h2>
      <div className="grid grid-cols-3 gap-2 max-w-[200px] mx-auto">
        {KEYPAD_LAYOUT.flat().map(digit => (
          <button
            key={digit}
            onClick={() => press(digit)}
            disabled={disabled}
            className={`h-12 rounded-lg border font-bold text-lg transition-all select-none
              ${sending === digit ? "bg-[#0F172A] text-white border-[#0F172A] scale-95" : "bg-[#F9FAFB] text-[#0F172A] border-[#E5E7EB] hover:bg-[#E5E7EB] active:bg-[#0F172A] active:text-white active:scale-95"}
              ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
            data-testid={`dtmf-${digit === "*" ? "star" : digit === "#" ? "hash" : digit}`}
          >
            <div className="leading-none">{digit}</div>
            {KEYPAD_LABELS[digit] && <div className="text-[8px] font-normal text-[#9CA3AF] tracking-widest leading-none mt-0.5">{KEYPAD_LABELS[digit]}</div>}
          </button>
        ))}
      </div>
    </div>
  );
}

const OUTCOME_COLORS_MINI = {
  interested: "text-green-700", vacancy_identified: "text-emerald-700", new_opportunity: "text-teal-700",
  callback: "text-blue-700", not_interested: "text-gray-500", no_answer: "text-yellow-700",
  voicemail: "text-yellow-600", busy: "text-orange-700", wrong_number: "text-red-700",
  do_not_contact: "text-red-800", existing_client: "text-purple-700", ended: "text-gray-500",
};

function RecentCallHistory({ onRedial }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/calls?limit=10").then(({ data }) => setCalls(data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="bg-white rounded-lg border border-[#E5E7EB] p-4"><div className="h-20 skeleton-shimmer rounded" /></div>;
  if (calls.length === 0) return null;

  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="recent-call-history">
      <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2 mb-3">
        <Clock size={16} className="text-[#C5A059]" /> Recent Calls
      </h2>
      <div className="space-y-1.5">
        {calls.map(c => {
          const time = c.created_at ? new Date(c.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "";
          const date = c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short" }) : "";
          const oc = c.outcome || c.call_status || "";
          return (
            <div key={c.id} className="flex items-center gap-2 py-1.5 border-b border-[#F3F4F6] last:border-0 group" data-testid={`recent-call-${c.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium text-[#0F172A] truncate">{c.contact_name || c.phone_number || "Unknown"}</div>
                <div className="flex items-center gap-1.5 text-[10px] text-[#9CA3AF]">
                  {c.phone_number && <span className="font-mono">{c.phone_number}</span>}
                  <span>{date} {time}</span>
                </div>
              </div>
              {oc && <span className={`text-[10px] font-medium capitalize ${OUTCOME_COLORS_MINI[oc] || "text-[#6B7280]"}`}>{oc.replace(/_/g, " ")}</span>}
              {c.phone_number && (
                <button
                  onClick={() => onRedial(c.phone_number, c.contact_name || "")}
                  className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-[#F3F4F6]"
                  title="Redial"
                  data-testid={`redial-${c.id}`}
                >
                  <ArrowClockwise size={14} className="text-[#6B7280]" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ConferenceButton({ callId }) {
  const [showPanel, setShowPanel] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState("");

  useEffect(() => {
    if (!showPanel) return;
    setLoading(true);
    api.get("/telephony/team-members-for-conference").then(({ data }) => setMembers(data)).catch(() => {}).finally(() => setLoading(false));
  }, [showPanel]);

  const addMember = async (uid, name) => {
    setAdding(uid);
    try {
      const { data } = await api.post(`/telephony/call/${callId}/conference`, { user_id: uid });
      if (data.success) toast.success(`${name} added to call`);
      else toast.error(data.error || "Failed to add");
    } catch (err) { toast.error(err?.response?.data?.detail || "Conference failed"); }
    setAdding("");
  };

  return (
    <div className="relative" data-testid="conference-panel">
      <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={() => setShowPanel(p => !p)} data-testid="conference-btn">
        <UsersThree size={14} /> Conference
      </Button>
      {showPanel && (
        <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-[#1E293B] border border-[#E5E7EB] dark:border-[#334155] rounded-lg shadow-lg z-20 p-3">
          <div className="text-xs font-semibold text-[#0F172A] dark:text-[#F1F5F9] mb-2">Add to call</div>
          {loading ? <div className="text-xs text-[#9CA3AF] py-2">Loading...</div> : (
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {members.filter(m => m.has_extension).map(m => (
                <button key={m.id} onClick={() => addMember(m.id, m.name)} disabled={adding === m.id}
                  className="w-full text-left flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#F3F4F6] dark:hover:bg-[#334155] text-xs transition-colors" data-testid={`conf-member-${m.id}`}>
                  <div className="w-5 h-5 rounded-full bg-[#0F172A] flex items-center justify-center text-white text-[8px] font-bold">{m.name?.[0]}</div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#0F172A] dark:text-[#F1F5F9] truncate">{m.name}</div>
                    <div className="text-[9px] text-[#9CA3AF]">Ext. {m.extension}</div>
                  </div>
                  {adding === m.id && <div className="w-3 h-3 border border-[#0F172A] border-t-transparent rounded-full animate-spin" />}
                </button>
              ))}
              {members.filter(m => m.has_extension).length === 0 && <div className="text-xs text-[#9CA3AF] py-2">No team members with extensions</div>}
            </div>
          )}
          <button onClick={() => setShowPanel(false)} className="mt-2 text-[10px] text-[#9CA3AF] hover:text-[#6B7280]">Close</button>
        </div>
      )}
    </div>
  );
}

function IvrPrompt() {
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => { const t = setTimeout(() => setDismissed(true), 12000); return () => clearTimeout(t); }, []);
  if (dismissed) return null;
  return (
    <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-center gap-2 animate-fade-in" data-testid="ivr-prompt">
      <Info size={16} className="text-blue-600 dark:text-blue-400 flex-shrink-0" />
      <span className="text-xs text-blue-700 dark:text-blue-300">Navigating an IVR / phone menu? Use the keypad below to enter options.</span>
      <button onClick={() => setDismissed(true)} className="ml-auto text-blue-400 hover:text-blue-600 text-xs">Dismiss</button>
    </div>
  );
}

const DEFAULT_SCRIPTS = {
  opening: ["Hi, this is [Your Name] from Banks Morgan Recruitment. How are you today?", "I'm reaching out because we specialise in placing professionals in your sector.", "Do you have a couple of minutes to discuss your current staffing needs?"],
  questions: ["Are you currently recruiting or expecting any vacancies in the next few months?", "What roles are typically hardest for you to fill?", "Do you currently work with recruitment agencies?", "What's most important to you when working with a recruitment partner?"],
  objections: ["I understand. Many of our clients felt the same before working with us. Could I send you our company profile?", "No problem at all. When would be a better time to call back?", "I appreciate that. If anything changes, we'd love the opportunity to help."],
  closing: ["Based on our chat, it sounds like we could really help. Can I arrange a meeting with one of our consultants?", "Can I take down the best contact for future vacancies?", "I'll follow up on [date] — is that convenient?"],
};

function EditableScriptPanel({ canEdit }) {
  const [sections, setSections] = useState(DEFAULT_SCRIPTS);
  const [editingSection, setEditingSection] = useState(null);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/scripts/dialler-scripts").then(({ data }) => {
      if (data.sections && Object.keys(data.sections).length > 0) setSections(s => ({ ...DEFAULT_SCRIPTS, ...data.sections }));
    }).catch(() => {});
  }, []);

  const startEdit = (key) => {
    setEditingSection(key);
    setEditText((sections[key] || []).join("\n"));
  };

  const saveSection = async (key) => {
    setSaving(true);
    const lines = editText.split("\n").map(l => l.trim()).filter(Boolean);
    const updated = { ...sections, [key]: lines };
    try {
      await api.put("/scripts/dialler-scripts", { sections: updated });
      setSections(updated);
      setEditingSection(null);
      toast.success("Script saved");
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  return (
    <div className="bg-white dark:bg-[#1E293B] rounded-lg border border-[#E5E7EB] dark:border-[#334155] p-4" data-testid="call-script-panel">
      <div className="flex items-center gap-2 mb-4">
        <Notepad size={18} className="text-[#C5A059]" />
        <span className="text-sm font-semibold text-[#0F172A] dark:text-[#F1F5F9]">Call Scripts</span>
      </div>
      {["opening", "questions", "objections", "closing"].map(key => (
        <div key={key} className="mb-3">
          <div className="flex items-center justify-between py-1.5">
            <span className="text-xs font-semibold text-[#C5A059] uppercase">{key}</span>
            {canEdit && editingSection !== key && (
              <button onClick={() => startEdit(key)} className="text-[#9CA3AF] hover:text-[#6B7280] p-0.5" data-testid={`edit-script-${key}`}>
                <PencilSimple size={12} />
              </button>
            )}
          </div>
          {editingSection === key ? (
            <div className="space-y-1.5">
              <Textarea value={editText} onChange={e => setEditText(e.target.value)} rows={4} className="text-xs" autoFocus />
              <div className="flex gap-1">
                <Button size="sm" className="h-6 text-[10px] gap-1" onClick={() => saveSection(key)} disabled={saving}><FloppyDisk size={10} /> Save</Button>
                <Button size="sm" variant="ghost" className="h-6 text-[10px]" onClick={() => setEditingSection(null)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {(sections[key] || []).map((line, i) => (
                <div key={i} className="p-2 bg-[#F9FAFB] dark:bg-[#0F172A] rounded border border-[#E5E7EB] dark:border-[#334155] text-xs text-[#374151] dark:text-[#CBD5E1] leading-relaxed cursor-pointer hover:border-[#C5A059] transition-colors"
                  onClick={() => { navigator.clipboard.writeText(line); toast.success("Copied"); }}>
                  {line}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function DialerPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mode, setMode] = useState("manual"); // manual | power
  const [manualNumber, setManualNumber] = useState("");
  const [contactName, setContactName] = useState("");
  const [activeCall, setActiveCall] = useState(null);
  const [callStatus, setCallStatus] = useState("idle");
  const [callDuration, setCallDuration] = useState(0);
  const [outcome, setOutcome] = useState("");
  const [callNotes, setCallNotes] = useState("");
  // Tracks whether the active call's outcome/notes have been committed via
  // POST /end. A call can reach a terminal status (no_answer/busy/ended) from
  // the 3CX side before the agent has typed notes or picked an outcome — this
  // must stay false until endCall() actually runs, so the UI never lets the
  // agent move on without saving.
  const [callSaved, setCallSaved] = useState(true);
  const [followUpDate, setFollowUpDate] = useState("");
  const [followUpTime, setFollowUpTime] = useState("");
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [showCreateVacancy, setShowCreateVacancy] = useState(false);
  const [micLevel, setMicLevel] = useState(0);

  // Power dialler state
  const [companies, setCompanies] = useState([]);
  const [powerList, setPowerList] = useState([]);
  const [powerIndex, setPowerIndex] = useState(0);
  const [powerActive, setPowerActive] = useState(false);
  const [powerPaused, setPowerPaused] = useState(false);

  const timerRef = useRef(null);
  const timerRunning = useRef(false);
  const autoCallDone = useRef(false);

  const loadCompanies = useCallback(async () => {
    try {
      const { data } = await api.get("/companies?limit=200");
      setCompanies(data.items || []);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadCompanies(); }, [loadCompanies]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopAudio();   // leaving the dialler must release the mic, not leave it running
    };
  }, []);

  // Auto-call from URL params (?phone=...&name=...&autoCall=1)
  useEffect(() => {
    const phone = searchParams.get("phone");
    const name = searchParams.get("name");
    const auto = searchParams.get("autoCall");
    if (phone) setManualNumber(phone);
    if (name) setContactName(name);
    if (auto === "1" && phone && !autoCallDone.current) {
      autoCallDone.current = true;
      setSearchParams({}, { replace: true }); // clear params
      setTimeout(() => initiateCall(phone, "", name || ""), 300);
    }
  // eslint-disable-next-line
  }, []);

  const startTimer = () => {
    if (timerRunning.current) return; // prevent double-start
    timerRunning.current = true;
    setCallDuration(0);
    timerRef.current = setInterval(() => setCallDuration(d => d + 1), 1000);
  };

  const stopTimer = () => {
    timerRunning.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const initiateCall = async (phoneNumber, companyId = "", cName = "") => {
    if (!phoneNumber) { toast.error("Enter a phone number"); return; }
    setCallStatus("calling");
    try {
      const { data } = await api.post("/telephony/call", {
        phone_number: phoneNumber,
        company_id: companyId,
        contact_name: cName || contactName,
        source: mode === "power" ? "power_dialler" : "manual",
      });
      setActiveCall(data);
      setCallSaved(false);

      if (data.audio_mode === "routepoint_bridge") {
        setCallStatus("requesting_mic");
        startAudio(data.id, (state, detail) => {
          if (state === "far_end_hangup") {
            // They put the phone down. Stop the clock and release the mic, but keep
            // the End Call button so the outcome and notes still get saved.
            stopTimer();
            toast.warning("The other person hung up — press End Call to save your notes");
            return;
          }
          if (state === "reconnecting_audio") {
            toast.warning("Audio connection dropped — reconnecting, keep talking");
            return;
          }
          if (state === "audio_disconnected") {
            stopTimer();
            toast.error("Your microphone is not reaching the call — please end the call and redial");
            return;
          }
          setCallStatus(state);
          if (state === "connected") {
            startTimer();
            toast.success("Connected — speaking through Hub");
          } else if (state === "error") {
            stopTimer();
            toast.error(detail || "Audio error");
          } else if (state === "no_answer") {
            stopTimer();
            toast.warning("No answer");
          } else if (state === "busy") {
            stopTimer();
            toast.warning("Line busy — engaged tone playing");
          } else if (state === "ended") {
            stopTimer();
          }
        }, (level) => setMicLevel(level));
      } else {
        setCallStatus(data.call_result?.status || "ringing");
        startTimer();
        toast.success(`Call initiated to ${phoneNumber}`);
      }
    } catch (err) {
      setCallStatus("failed");
      const msg = err.response?.data?.detail || "Call failed";
      toast.error(msg);
    }
  };

  const endCall = async () => {
    stopTimer();
    stopAudio();
    if (!activeCall) { setCallStatus("idle"); return; }
    try {
      const { data } = await api.post(`/telephony/call/${activeCall.id}/end`, {
        call_status: outcome || "ended",
        duration: callDuration,
        notes: callNotes,
        outcome: outcome,
        company_id: activeCall.company_id,
        contact_name: activeCall.contact_name,
        phone_number: activeCall.phone_number,
        follow_up_date: followUpDate,
        follow_up_time: followUpTime,
        follow_up_notes: followUpNotes,
      });
      setCallStatus("ended");
      setCallSaved(true);
      if (data.disconnect_error) {
        toast.warning(data.disconnect_error);
      }
      if (data.suggest_create_vacancy || outcome === "vacancy_identified") {
        setShowCreateVacancy(true);
      }
      toast.success("Call logged");
    } catch (err) { toast.error("Failed to log call"); }
  };

  // Autosave call notes in the background as the agent types, so notes are
  // never lost if the call ends on the far side (no_answer/busy/hangup)
  // before End Call is pressed, or if the agent steps away without pressing
  // anything at all.
  useEffect(() => {
    if (!activeCall || callSaved) return;
    const t = setTimeout(() => {
      api.patch(`/calls/${activeCall.id}/notes`, { notes: callNotes }).catch(() => {});
    }, 1200);
    return () => clearTimeout(t);
  }, [callNotes, activeCall, callSaved]);

  const resetCall = () => {
    stopAudio(); // Ensure audio is cleaned up
    setActiveCall(null);
    setCallStatus("idle");
    setCallSaved(true);
    setCallDuration(0);
    setOutcome("");
    setCallNotes("");
    setFollowUpDate("");
    setFollowUpTime("");
    setFollowUpNotes("");
    setShowCreateVacancy(false);
    setManualNumber("");
    setContactName("");
  };

  // Power dialler
  const startPowerDialler = () => {
    const list = companies.filter(c => c.phone);
    if (list.length === 0) { toast.error("No companies with phone numbers"); return; }
    setPowerList(list);
    setPowerIndex(0);
    setPowerActive(true);
    setPowerPaused(false);
  };

  const powerNext = async () => {
    if (!callSaved) await endCall();
    resetCall();
    const next = powerIndex + 1;
    if (next >= powerList.length) { toast.success("Power dialler complete — all contacts called"); setPowerActive(false); return; }
    setPowerIndex(next);
  };

  const powerCall = () => {
    const c = powerList[powerIndex];
    if (!c) return;
    initiateCall(c.phone, c.id, c.contact_person || c.name);
  };

  const formatTime = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;
  const currentPowerCompany = powerList[powerIndex];
  const statusInfo = STATUS_DISPLAY[callStatus] || STATUS_DISPLAY.idle;
  const StatusIcon = statusInfo.icon;

  return (
    <div data-testid="dialer-page" className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-[#0F172A]">Recruitment Dialler</h1>
          <p className="text-sm text-[#6B7280]">
            {mode === "manual" ? "Manual dial — search and call any contact" : `Power Dialler — ${powerList.length} contacts queued`}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-[#F3F4F6] px-3 py-1.5 rounded-full">
            <Keyboard size={14} className="text-[#6B7280]" />
            <span className={`text-xs font-medium ${mode === "manual" ? "text-[#0F172A]" : "text-[#9CA3AF]"}`}>Manual</span>
            <Switch checked={mode === "power"} onCheckedChange={(c) => { setMode(c ? "power" : "manual"); if (!c) setPowerActive(false); }} data-testid="dialer-mode-toggle" />
            <span className={`text-xs font-medium ${mode === "power" ? "text-[#0F172A]" : "text-[#9CA3AF]"}`}>Power</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Left — Contact / Dial Panel */}
        <div className="space-y-4">
          {mode === "manual" ? (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 space-y-3" data-testid="manual-dial-panel">
              <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
                <Hash size={16} className="text-[#C5A059]" /> Manual Dial
              </h2>
              <Input placeholder="Phone number" value={manualNumber} onChange={e => setManualNumber(e.target.value)}
                className="text-lg font-mono" data-testid="manual-number-input" />
              <Input placeholder="Contact name (optional)" value={contactName} onChange={e => setContactName(e.target.value)}
                className="text-sm" data-testid="contact-name-input" />
            </div>
          ) : powerActive && currentPowerCompany ? (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="power-dialler-contact">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
                  <Buildings size={16} className="text-[#1E3A8A]" /> {currentPowerCompany.name}
                </h2>
                <Badge className="text-[10px] bg-[#0F172A] text-[#C5A059]">{powerIndex + 1} / {powerList.length}</Badge>
              </div>
              <div className="space-y-1 text-sm text-[#6B7280]">
                {currentPowerCompany.contact_person && <p><span className="font-medium text-[#374151]">Contact:</span> {currentPowerCompany.contact_person}</p>}
                <p><span className="font-medium text-[#374151]">Phone:</span> {currentPowerCompany.phone}</p>
                {currentPowerCompany.email && <p><span className="font-medium text-[#374151]">Email:</span> {currentPowerCompany.email}</p>}
                {currentPowerCompany.sector && <p><span className="font-medium text-[#374151]">Sector:</span> {currentPowerCompany.sector}</p>}
                {currentPowerCompany.last_contacted && <p><span className="font-medium text-[#374151]">Last Contact:</span> {new Date(currentPowerCompany.last_contacted).toLocaleDateString()}</p>}
              </div>
            </div>
          ) : mode === "power" && !powerActive ? (
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-6 text-center" data-testid="power-dialler-start">
              <Phone size={36} className="text-[#D1D5DB] mx-auto mb-3" />
              <p className="text-sm text-[#6B7280] mb-3">{companies.filter(c => c.phone).length} companies with phone numbers</p>
              <Button onClick={startPowerDialler} className="bg-[#10B981] hover:bg-[#059669] gap-2" data-testid="start-power-btn">
                <Play size={16} weight="fill" /> Start Power Dialler
              </Button>
            </div>
          ) : null}

          {/* Call Status + Controls */}
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4" data-testid="call-controls">
            <div className="text-center mb-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium text-white ${statusInfo.color}`}>
                <StatusIcon size={16} weight="bold" /> {statusInfo.label}
              </div>
              <p className="text-3xl font-bold text-[#0F172A] mt-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {formatTime(callDuration)}
              </p>
              {(callStatus === "connected" || callStatus === "answered") && (
                <div className="mt-2 flex items-center justify-center gap-2" data-testid="mic-level-meter">
                  <Microphone size={14} className="text-[#6B7280]" />
                  <div className="w-32 h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-75"
                      style={{
                        width: `${Math.min(100, micLevel * 500)}%`,
                        backgroundColor: micLevel > 0.05 ? "#10B981" : "#D1D5DB",
                      }}
                    />
                  </div>
                  <span className="text-[10px] text-[#6B7280] w-6">{micLevel > 0.01 ? "MIC" : ""}</span>
                </div>
              )}
            </div>
            <div className="flex justify-center gap-3 flex-wrap">
              {!activeCall || callSaved ? (
                <Button onClick={() => {
                  if (mode === "manual") initiateCall(manualNumber);
                  else if (powerActive) powerCall();
                }} className="gap-2 bg-[#10B981] hover:bg-[#059669] px-8" data-testid="call-btn" disabled={mode === "manual" && !manualNumber}>
                  <Phone size={18} weight="fill" /> Call
                </Button>
              ) : (
                <Button onClick={endCall} className="gap-2 bg-[#EF4444] hover:bg-[#DC2626] px-8" data-testid="end-call-btn">
                  <Phone size={18} weight="fill" /> End Call
                </Button>
              )}
              {powerActive && (
                <>
                  <Button onClick={powerNext} variant="outline" className="gap-1" data-testid="next-contact-btn">
                    <SkipForward size={16} /> Next
                  </Button>
                  <Button onClick={() => { setPowerPaused(!powerPaused); }} variant="outline" className="gap-1" data-testid="pause-power-btn">
                    <Pause size={14} /> {powerPaused ? "Resume" : "Pause"}
                  </Button>
                  <Button onClick={() => { setPowerActive(false); resetCall(); }} variant="outline" className="gap-1 border-[#EF4444] text-[#EF4444]" data-testid="stop-power-btn">
                    <Stop size={14} /> Stop
                  </Button>
                </>
              )}
              {callStatus === "ended" && !powerActive && callSaved && (
                <Button onClick={resetCall} variant="outline" className="gap-1" data-testid="new-call-btn">
                  <ArrowRight size={14} /> New Call
                </Button>
              )}
              {(callStatus === "connected" || callStatus === "answered") && activeCall && (
                <ConferenceButton callId={activeCall.id} />
              )}
              {(callStatus === "connected" || callStatus === "answered") && activeCall && (
                <TransferButton callId={activeCall.id} />
              )}
            </div>
          </div>

          {/* IVR Prompt — visible when call connects */}
          {(callStatus === "connected" || callStatus === "answered") && <IvrPrompt />}

          {/* DTMF Keypad — visible during connected calls */}
          {(callStatus === "connected" || callStatus === "answered") && activeCall && (
            <DtmfKeypad callId={activeCall.id} disabled={false} />
          )}

          {/* Recent Call History */}
          {callStatus === "idle" && !powerActive && (
            <RecentCallHistory onRedial={(phone, name) => { setManualNumber(phone); setContactName(name); }} />
          )}
        </div>

        {/* Middle — Call Outcome + Notes */}
        <div className="space-y-4">
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 space-y-3" data-testid="call-outcome-panel">
            <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <Notepad size={16} className="text-[#C5A059]" /> Call Outcome
            </h2>
            <Select value={outcome} onValueChange={setOutcome}>
              <SelectTrigger data-testid="outcome-select"><SelectValue placeholder="Select outcome..." /></SelectTrigger>
              <SelectContent>
                {CALL_OUTCOMES.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Call notes..." value={callNotes} onChange={e => setCallNotes(e.target.value)}
              rows={4} data-testid="call-notes-input" />

            {showCreateVacancy && (
              <div className="p-3 bg-[#F0FDF4] border border-green-200 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">Vacancy Identified!</p>
                <Button size="sm" className="bg-green-600 hover:bg-green-700 gap-1 text-sm"
                  onClick={() => window.location.href = `/vacancies`} data-testid="create-vacancy-from-call-btn">
                  Create Vacancy <ArrowRight size={14} />
                </Button>
              </div>
            )}
          </div>

          {/* Follow-up */}
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 space-y-3" data-testid="follow-up-panel">
            <h2 className="text-sm font-semibold text-[#0F172A] flex items-center gap-2">
              <CalendarBlank size={16} className="text-[#C5A059]" /> Follow-up
            </h2>
            <div className="grid grid-cols-2 gap-2">
              <Input type="date" value={followUpDate} onChange={e => setFollowUpDate(e.target.value)} data-testid="followup-date" />
              <Input type="time" value={followUpTime} onChange={e => setFollowUpTime(e.target.value)} data-testid="followup-time" />
            </div>
            <Textarea placeholder="Follow-up notes..." value={followUpNotes} onChange={e => setFollowUpNotes(e.target.value)}
              rows={2} className="text-sm" data-testid="followup-notes" />
          </div>
        </div>

        {/* Right — Call Script (editable for admin/TL) */}
        <EditableScriptPanel canEdit={user?.role === "admin" || user?.role === "team_leader"} />

        {/* Mr Banks AI Call Coach */}
        <MrBanksPanel callId={activeCall?.id} callStatus={callStatus} transcript={callNotes} />
      </div>

      {/* Audio Diagnostic Panel (admin only) */}
      {user?.role === "admin" && (
        <AudioDiagnosticPanel activeCall={activeCall} callStatus={callStatus} />
      )}
    </div>
  );
}

function AudioDiagnosticPanel({ activeCall, callStatus }) {
  const [open, setOpen] = useState(false);
  const [diag, setDiag] = useState(null);
  const [loopback, setLoopback] = useState(false);
  const [toneStatus, setToneStatus] = useState("");
  const pollRef = useRef(null);

  // Poll diagnostic when a call is active
  useEffect(() => {
    if (open && activeCall?.id && (callStatus === "connected" || callStatus === "answered")) {
      pollRef.current = setInterval(async () => {
        try {
          const { data } = await api.get(`/telephony/audio/diagnostic/${activeCall.id}`);
          setDiag(data);
        } catch (_ed) { /* ok */ }
      }, 1000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open, activeCall, callStatus]);

  const toggleLoopback = async () => {
    if (loopback) {
      stopMicLoopback();
      setLoopback(false);
      toast.info("Loopback stopped");
    } else {
      const info = await startMicLoopback();
      setLoopback(true);
      toast.success(`Loopback ON (${info.sampleRate}Hz, ${info.state})`);
    }
  };

  const runTestTone = async () => {
    setToneStatus("playing...");
    try {
      const r = await playTestTone();
      setToneStatus(r.error ? `Error: ${r.error}` : `Done — ${r.totalBytes} bytes played`);
    } catch (e) {
      setToneStatus(`Error: ${e.message}`);
    }
  };

  return (
    <div className="mt-4 bg-white rounded-lg border border-[#E5E7EB]" data-testid="audio-diagnostic-panel">
      <button onClick={() => setOpen(!open)} className="w-full p-3 text-left flex items-center justify-between">
        <span className="text-xs font-semibold text-[#6B7280] uppercase tracking-wide">Audio Diagnostic</span>
        <span className="text-xs text-[#9CA3AF]">{open ? "−" : "+"}</span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          {/* Diagnostic tools */}
          <div className="flex gap-2">
            <Button size="sm" variant={loopback ? "destructive" : "outline"} onClick={toggleLoopback} className="text-xs h-7">
              {loopback ? "Stop Loopback" : "A. Mic Loopback"}
            </Button>
            <Button size="sm" variant="outline" onClick={runTestTone} className="text-xs h-7">
              B. Test Tone
            </Button>
            {toneStatus && <span className="text-[10px] text-[#6B7280] self-center">{toneStatus}</span>}
          </div>

          {/* Live bridge stats */}
          {diag && !diag.error && (
            <div className="font-mono text-[10px] bg-[#0F172A] text-green-400 rounded p-3 space-y-1 overflow-x-auto">
              <div className="text-white font-bold mb-1">Bridge: {diag.dn}/{diag.participant_id} | {diag.elapsed_sec}s | {diag.running ? "RUNNING" : diag.stopped ? "STOPPED (persisted)" : "STOPPED"}</div>
              <div className="text-yellow-300 font-bold">── UPSTREAM (Mic → 3CX) ──</div>
              <div>Browser→Backend: {diag.upstream.browser_recv_chunks} chunks, {diag.upstream.browser_recv_bytes} bytes, RMS peak={diag.upstream.browser_recv_rms_peak}</div>
              {diag.upstream.browser_first_samples?.length > 0 && (
                <div className="text-gray-400">  first samples: [{diag.upstream.browser_first_samples.slice(0,10).join(", ")}]</div>
              )}
              <div>Backend→3CX:     {diag.upstream.to_3cx_frames} frames, {diag.upstream.to_3cx_bytes} bytes ({diag.upstream.to_3cx_bps} B/s), silence={diag.upstream.to_3cx_silence_frames}</div>
              <div>                  RMS peak={diag.upstream.to_3cx_rms_peak}, buffer={diag.upstream.buffer_bytes}B</div>
              {diag.upstream.to_3cx_first_samples?.length > 0 && (
                <div className="text-gray-400">  first 3CX samples: [{diag.upstream.to_3cx_first_samples.slice(0,10).join(", ")}]</div>
              )}
              <div>POST status: <span className={diag.upstream.to_3cx_status === "failed" ? "text-red-400" : ""}>{diag.upstream.to_3cx_status}</span></div>
              {diag.upstream.to_3cx_error && <div className="text-red-400">ERROR: {diag.upstream.to_3cx_error}</div>}
              <div className="text-yellow-300 font-bold mt-1">── DOWNSTREAM (3CX → Speakers) ──</div>
              <div>3CX→Backend:     {diag.downstream.from_3cx_chunks} chunks, {diag.downstream.from_3cx_bytes} bytes ({diag.downstream.from_3cx_bps} B/s), RMS peak={diag.downstream.from_3cx_rms_peak}</div>
              {diag.downstream.from_3cx_first_samples?.length > 0 && (
                <div className="text-gray-400">  first samples: [{diag.downstream.from_3cx_first_samples.slice(0,10).join(", ")}]</div>
              )}
              <div>Backend→Browser:  {diag.downstream.to_browser_chunks} chunks, {diag.downstream.to_browser_bytes} bytes, drops={diag.downstream.queue_drops}</div>
              <div>GET status:  <span className={diag.downstream.from_3cx_status === "failed" ? "text-red-400" : ""}>{diag.downstream.from_3cx_status}</span></div>
              {diag.downstream.from_3cx_error && <div className="text-red-400">ERROR: {diag.downstream.from_3cx_error}</div>}
            </div>
          )}
          {diag?.error && (
            <div className="text-xs text-red-600">No active bridge. Active: {JSON.stringify(diag.active_bridges)}</div>
          )}
          {!diag && (callStatus === "connected" || callStatus === "answered") && (
            <div className="text-xs text-[#6B7280]">Polling bridge stats...</div>
          )}
        </div>
      )}
    </div>
  );
}
